// Persisted date presets + bounds for Operations (screenings, tasks/shifts,
// programmation groups). Separate from `analysisFilters` (stats + financial analysis).

import moment from 'moment';
import { resolve_date_range_preset } from '../../components/DateRangePresets/dateRangePresets';
import { readPersistedSessionIds } from '../../services/kronosPrefs/sessionIds';
import {
    emptyUserInstancePrefs,
    mergeUserInstancePrefsPatch,
    readUserInstancePrefsDoc,
} from '../../services/kronosPrefs/userInstancePrefs';

/** Historical flat localStorage key (pre-namespaced prefs); no longer written by the app. */
export const EXPLOITATION_DATE_UI_STORAGE_KEY = 'kronos.exploitation.dateUi';

const SET_SCREENINGS = 'kronos/exploitationDateUi/SET_SCREENINGS';
const SET_TASKS = 'kronos/exploitationDateUi/SET_TASKS';
const SET_PROGRAMMATION = 'kronos/exploitationDateUi/SET_PROGRAMMATION';

function default_screenings() {
    const r = resolve_date_range_preset('now', moment());
    return {
        date_preset: 'now',
        start_at_gte: r.start.toISOString(),
        stop_at_lte: r.end ? r.end.toISOString() : null,
    };
}

function default_tasks() {
    const r = resolve_date_range_preset('now', moment());
    return {
        date_preset: 'now',
        start_at_gte: r.start.toISOString(),
        start_at_lte: r.end ? r.end.toISOString() : null,
    };
}

function default_programmation() {
    const r = resolve_date_range_preset('now', moment());
    return {
        date_preset: 'now',
        from: r.start.toISOString(),
        to: r.end ? r.end.toISOString() : '',
    };
}

function read_persisted() {
    if (typeof window === 'undefined' || !window.localStorage) {
        return null;
    }
    try {
        const { userId, instanceId } = readPersistedSessionIds();
        if (!userId) {
            return null;
        }
        const doc = readUserInstancePrefsDoc(userId, instanceId) || emptyUserInstancePrefs();
        const blob = doc.exploitationDateUi;
        return blob && typeof blob === 'object' ? blob : null;
    } catch (_e) {
        return null;
    }
}

function resolve_screenings_blob(blob) {
    if (!blob || typeof blob !== 'object')
        return default_screenings();
    const preset = typeof blob.date_preset === 'string' ? blob.date_preset : 'now';
    if (preset && preset !== 'custom') {
        const r = resolve_date_range_preset(preset, moment());
        if (r?.start) {
            return {
                date_preset: preset,
                start_at_gte: r.start.toISOString(),
                stop_at_lte: r.end ? r.end.toISOString() : null,
            };
        }
    }
    return {
        date_preset: 'custom',
        start_at_gte: blob.start_at_gte || default_screenings().start_at_gte,
        stop_at_lte: Object.prototype.hasOwnProperty.call(blob, 'stop_at_lte') ? blob.stop_at_lte : null,
    };
}

function resolve_tasks_blob(blob) {
    if (!blob || typeof blob !== 'object')
        return default_tasks();
    const preset = typeof blob.date_preset === 'string' ? blob.date_preset : 'now';
    if (preset && preset !== 'custom') {
        const r = resolve_date_range_preset(preset, moment());
        if (r?.start) {
            return {
                date_preset: preset,
                start_at_gte: r.start.toISOString(),
                start_at_lte: r.end ? r.end.toISOString() : null,
            };
        }
    }
    return {
        date_preset: 'custom',
        start_at_gte: blob.start_at_gte || default_tasks().start_at_gte,
        start_at_lte: blob.start_at_lte || default_tasks().start_at_lte,
    };
}

function resolve_programmation_blob(blob) {
    if (!blob || typeof blob !== 'object')
        return default_programmation();
    const preset = typeof blob.date_preset === 'string' ? blob.date_preset : 'now';
    if (preset && preset !== 'custom') {
        const r = resolve_date_range_preset(preset, moment());
        if (r?.start) {
            return {
                date_preset: preset,
                from: r.start.toISOString(),
                to: r.end ? r.end.toISOString() : '',
            };
        }
    }
    return {
        date_preset: 'custom',
        from: blob.from || default_programmation().from,
        to: Object.prototype.hasOwnProperty.call(blob, 'to') ? blob.to : '',
    };
}

function build_initial() {
    const p = read_persisted();
    if (!p || typeof p !== 'object') {
        return {
            screenings: default_screenings(),
            tasks: default_tasks(),
            programmation: default_programmation(),
        };
    }
    return {
        screenings: resolve_screenings_blob(p.screenings),
        tasks: resolve_tasks_blob(p.tasks),
        programmation: resolve_programmation_blob(p.programmation),
    };
}

const initialState = build_initial();

export default function exploitationDateUi(state = initialState, action) {
    switch (action.type) {
        case SET_SCREENINGS:
            return { ...state, screenings: { ...state.screenings, ...action.payload } };
        case SET_TASKS:
            return { ...state, tasks: { ...state.tasks, ...action.payload } };
        case SET_PROGRAMMATION:
            return { ...state, programmation: { ...state.programmation, ...action.payload } };
        default:
            return state;
    }
}

export function persistExploitationDateUiMiddleware({ getState }) {
    return (next) => (action) => {
        const out = next(action);
        if (
            typeof window !== 'undefined'
            && window.localStorage
            && (action.type === SET_SCREENINGS || action.type === SET_TASKS || action.type === SET_PROGRAMMATION)
        ) {
            try {
                const u = getState().auth?.user;
                const userId = u?.id != null ? String(u.id) : null;
                const instanceId = u?.tkt_instance?.id != null ? String(u.tkt_instance.id) : 'none';
                if (userId) {
                    mergeUserInstancePrefsPatch(userId, instanceId, {
                        exploitationDateUi: getState().exploitationDateUi,
                    });
                }
            } catch (_e) {
                /* ignore */
            }
        }
        return out;
    };
}

export const setExploitationScreeningsDate = (payload) => ({ type: SET_SCREENINGS, payload });
export const setExploitationTasksDate = (payload) => ({ type: SET_TASKS, payload });
export const setExploitationProgrammationDate = (payload) => ({ type: SET_PROGRAMMATION, payload });
